import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MapPin, Check, AlertTriangle, Loader2, Package } from 'lucide-react';
import { PListOrderDto } from '../../../../../services/plistOrderService';
import { packingService } from '../../../../../services/packingService';
import { PackZoneDto } from '../../../../../types/packing';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface PackZoneSelectionModalProps {
  isOpen: boolean;
  pListId: number;
  pListOrders: PListOrderDto[];
  onConfirm: (packZoneID: string) => Promise<void>;
  onClose: () => void;
}

/**
 * PackZoneSelectionModal - Modal for selecting a pack zone (pakkesone)
 * when sending a picking list to packing.
 * Supports both barcode scanning and dropdown selection.
 */
export const PackZoneSelectionModal: React.FC<PackZoneSelectionModalProps> = ({
  isOpen,
  pListId,
  pListOrders,
  onConfirm,
  onClose
}) => {
  const { t } = useTranslation();
  const [packZones, setPackZones] = useState<PackZoneDto[]>([]);
  const [packageCounts, setPackageCounts] = useState<Record<number, number>>({});
  const [selectedPackZoneID, setSelectedPackZoneID] = useState<string>('');
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Load data when modal opens
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [zones, counts] = await Promise.all([
        packingService.getPackZones(),
        packingService.getPackageCountsByPList(pListId)
      ]);
      setPackZones(zones);
      setPackageCounts(counts);
    } catch (err) {
      console.error('Error loading pack zone data:', err);
      setError(t('warehouse.modules.picking.packZone.loadError'));
    } finally {
      setLoading(false);
    }
  }, [pListId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedPackZoneID('');
      setScanValue('');
      setScanError(null);
      setScanSuccess(false);
      setIsSubmitting(false);
      loadData();
    }
  }, [isOpen, loadData]);

  // Focus scan input when ready
  useEffect(() => {
    if (isOpen && !loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, loading]);

  // Handle scan input change
  const handleScanInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanValue(e.target.value);
    setScanError(null);
    setScanSuccess(false);
  };

  // Handle Enter key on scan input
  const handleScanKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanConfirm();
    }
  };

  // Validate scanned value against pack zones
  const handleScanConfirm = () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;

    const matchedZone = packZones.find(
      z => z.packZoneID.toLowerCase() === trimmed.toLowerCase()
    );

    if (matchedZone) {
      setSelectedPackZoneID(matchedZone.packZoneID);
      setScanSuccess(true);
      setScanError(null);
      setScanValue('');
    } else {
      setScanError(t('warehouse.modules.picking.packZone.unknownZone', { 0: trimmed }));
      setScanSuccess(false);
    }
  };

  // Handle dropdown selection
  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPackZoneID(e.target.value);
    setScanError(null);
    setScanSuccess(false);
    setScanValue('');
  };

  // Handle confirm
  const handleConfirm = async () => {
    if (!selectedPackZoneID || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onConfirm(selectedPackZoneID);
    } catch (err) {
      console.error('Error confirming pack zone:', err);
      setError(t('warehouse.modules.picking.packZone.sendError'));
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Calculate total kolli count
  const totalKolli = pListOrders.reduce(
    (sum, order) => sum + (packageCounts[order.orderNo] || 0),
    0
  );

  const selectedZone = packZones.find(z => z.packZoneID === selectedPackZoneID);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <MapPin className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.picking.packZone.title')}</h3>
              <p className="text-sm text-gray-500">{t('warehouse.modules.picking.pickingList')} #{pListId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5 max-h-[calc(100vh-200px)] sm:max-h-[calc(100vh-280px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              <span>{t('warehouse.modules.picking.packZone.loading')}</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle size={20} />
                <span>{error}</span>
                <button onClick={loadData} className="ml-auto text-sm underline">
                  {t('common.buttons.tryAgain')}
                </button>
              </div>
            </div>
          ) : packZones.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-700">
                <AlertTriangle size={20} />
                <span>{t('warehouse.modules.picking.packZone.noZones')}</span>
              </div>
            </div>
          ) : (
            <>
              {/* Order summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package size={18} className="text-gray-600" />
                  <h4 className="font-medium text-gray-800">
                    {t('warehouse.modules.picking.packZone.ordersSummary', { 0: pListOrders.length, 1: totalKolli })}
                  </h4>
                </div>
                <div className="space-y-1">
                  {pListOrders.map(order => (
                    <div key={order.orderNo} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {t('warehouse.modules.picking.packZone.orderNo', { 0: order.orderNo })}
                        {order.customerName && (
                          <span className="text-gray-400 ml-1">- {order.customerName}</span>
                        )}
                      </span>
                      <span className="text-gray-600 font-medium">
                        {t('warehouse.modules.picking.packZone.kolliCount', { 0: packageCounts[order.orderNo] || 0 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scan input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('warehouse.modules.picking.packZone.scanLabel')}
                </label>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={scanValue}
                    onChange={handleScanInput}
                    onKeyDown={handleScanKeyDown}
                    disabled={isSubmitting}
                    className={`flex-1 px-4 py-3 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
                      scanError
                        ? 'border-red-500 bg-red-50 focus:border-red-600'
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder={t('warehouse.modules.picking.packZone.scanPlaceholder')}
                    autoComplete="off"
                  />
                  <button
                    onClick={handleScanConfirm}
                    disabled={isSubmitting || !scanValue.trim()}
                    className={`px-5 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                      isSubmitting || !scanValue.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Check size={20} />
                  </button>
                </div>
                {scanError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <AlertTriangle className="text-red-600 flex-shrink-0" size={18} />
                    <p className="text-sm text-red-800">{scanError}</p>
                  </div>
                )}
              </div>

              {/* Dropdown */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('warehouse.modules.picking.packZone.orSelectFromList')}
                </label>
                <select
                  value={selectedPackZoneID}
                  onChange={handleDropdownChange}
                  disabled={isSubmitting}
                  className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg text-base bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('warehouse.modules.picking.packZone.selectPlaceholder')}</option>
                  {packZones.map(zone => (
                    <option key={zone.packZoneID} value={zone.packZoneID}>
                      {zone.packZoneID}{zone.description ? ` - ${zone.description}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected zone confirmation */}
              {selectedPackZoneID && selectedZone && (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-green-900 text-lg">{selectedZone.packZoneID}</p>
                      {selectedZone.description && (
                        <p className="text-sm text-green-700">{selectedZone.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2 sm:order-1"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedPackZoneID || isSubmitting || loading}
            className={`px-6 py-3 sm:py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors min-h-[44px] order-1 sm:order-2 ${
              !selectedPackZoneID || isSubmitting || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {t('warehouse.modules.picking.packZone.sending')}
              </>
            ) : (
              <>
                <Check size={18} />
                {t('warehouse.modules.picking.packZone.confirmAndSend')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
